import { useEffect, useRef, useState } from 'react';
import api from './api.js';
import AdminPanel from './AdminPanel.js';
import './App.css';

export const actions = [
  { key: 'pay', icon: '💸', label: 'Pay', roles: ['admin', 'cashier'] },
  { key: 'check', icon: '✅', label: 'Check', roles: ['admin', 'manager', 'cashier', 'topup', 'finance', 'auditor'] },
  { key: 'topup', icon: '⬆️', label: 'Top up', roles: ['admin', 'topup', 'finance'] },
  { key: 'create', icon: '➕', label: 'Create', roles: ['admin', 'finance'] },
  { key: 'history', icon: '📜', label: 'History', roles: ['admin', 'manager', 'cashier', 'topup', 'finance', 'auditor'] },
  { key: 'admin', icon: '⚙️', label: 'Admin', roles: ['admin', 'manager', 'finance', 'auditor'] },
  { key: 'refund', icon: '↩️', label: 'Refund', roles: ['cashier'] },
];

function CardPhotoChooser({ photo, onChoose, onError }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);

  useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
    return () => stream?.getTracks().forEach((track) => track.stop());
  }, [stream]);

  const startCamera = async () => {
    try { setStream(await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })); }
    catch { onError('Camera access was unavailable. Allow camera permission or choose a photo file.'); }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return onError('Wait for the camera preview, then try again.');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return onError('The camera image could not be captured.');
      onChoose(new File([blob], `cardholder-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      stream?.getTracks().forEach((track) => track.stop()); setStream(null);
    }, 'image/jpeg', 0.9);
  };

  return <div className="cardPhotoChooser"><div className="photoChoices"><button type="button" className="photoChoiceButton" onClick={startCamera} disabled={Boolean(stream)}>Use webcam</button><span>or</span><label>Choose photo file<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => onChoose(event.target.files?.[0] || null)} /></label></div>{stream && <div className="cameraPreview"><video ref={videoRef} autoPlay playsInline muted /><div><button type="button" className="popupClose adminPrimary" onClick={capture}>Capture photo</button><button type="button" className="popupClose" onClick={() => { stream.getTracks().forEach((track) => track.stop()); setStream(null); }}>Cancel camera</button></div></div>}{photo && <p className="selectedPhoto">Selected photo: {photo.name}</p>}</div>;
}
const KEYPAD_DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
export const formatHistoryTime = (value) => new Date(value).toLocaleTimeString('en-US', {
  timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
});
export const historyRowClass = (type) => type === 'charge'
  ? 'historyRow historyRowCharge'
  : type === 'topup'
    ? 'historyRow historyRowTopup'
    : ['opening_balance', 'legacy_opening_balance', 'balance'].includes(type)
      ? 'historyRow historyRowBalance'
      : 'historyRow';

function Receipt({ receipt }) {
  return (
    <section className="receipt" aria-label="Transaction receipt">
      <p className="receiptType">{receipt.type === 'topup' ? 'Top-up receipt' : receipt.type === 'reversal' ? 'Reversal receipt' : receipt.type === 'refund' ? 'Refund receipt' : 'Payment receipt'}</p>
      <strong className="receiptAmount">{Number(receipt.amount).toFixed(2)} {receipt.currency}</strong>
      <dl><div><dt>Reference</dt><dd><code>{receipt.paymentReference}</code></dd></div><div><dt>Card</dt><dd>{receipt.owner ? `${receipt.owner} · ` : ''}{receipt.cardId}</dd></div><div><dt>Operator</dt><dd>{receipt.operator}</dd></div><div><dt>Time</dt><dd>{new Date(receipt.occurredAt).toLocaleString()}</dd></div></dl>
      <button type="button" className="popupClose receiptPrint" onClick={() => window.print()}>Print receipt</button>
    </section>
  );
}

function TouchNumberPad({ value, onChange, label, maxLength, decimal = false, maskedLength = 0, suffix = '' }) {
  const append = (character) => {
    if (!/^\d$/.test(character) && !(decimal && character === '.')) return;
    if (decimal && character === '.' && value.includes('.')) return;
    if (decimal && value.split('.')[1]?.length >= 2) return;
    if (!decimal && value.length >= maxLength) return;
    const next = `${value}${character}`;
    onChange(decimal ? next.replace(/^0(?=\d)/, '').slice(0, maxLength) : next.slice(0, maxLength));
  };
  const handleKeyDown = (event) => {
    if (/^\d$/.test(event.key) || (decimal && event.key === '.')) {
      event.preventDefault();
      append(event.key);
    } else if (event.key === 'Backspace') {
      event.preventDefault();
      onChange(value.slice(0, -1));
    } else if (event.key === 'Delete' || event.key === 'Escape') {
      event.preventDefault();
      onChange('');
    }
  };
  return (
    <div className="touchNumberPad" onKeyDown={handleKeyDown} tabIndex="0" role="group" aria-label={`${label} keypad. Use number keys, Backspace, or Delete.`}>
      {maskedLength ? (
        <output className="pinDisplay" aria-label={`${label}: ${value.length} of ${maskedLength} digits entered`}>
          {Array.from({ length: maskedLength }, (_, index) => <span key={index} aria-hidden="true" className={index < value.length ? 'filled' : ''}>{index < value.length ? '●' : '○'}</span>)}
        </output>
      ) : <output aria-label={label}>{value || '0'} {suffix && <small>{suffix}</small>}</output>}
      <div className="numberPad" aria-label={`${label} keypad`}>
        {KEYPAD_DIGITS.map((digit) => <button key={digit} type="button" onClick={() => append(String(digit))}>{digit}</button>)}
        {decimal
          ? <button type="button" className="decimalKey" onClick={() => append('.')} aria-label="Decimal point">.</button>
          : <span className="numberPadSpacer" aria-hidden="true" />}
        <button type="button" onClick={() => append('0')}>0</button>
        <button type="button" onClick={() => onChange(value.slice(0, -1))} aria-label="Backspace">⌫</button>
      </div>
    </div>
  );
}

function AmountPad({ value, onChange }) {
  return <TouchNumberPad value={value} onChange={onChange} label="Amount" maxLength={11} decimal suffix="THB" />;
}

function PinPad({ value, onChange, length = 6, label = 'PIN' }) {
  return <TouchNumberPad value={value} onChange={onChange} label={label} maxLength={length} maskedLength={length} />;
}

function CardTouchStep({ authorization = false, value, onChange, onContinue, busy, finalAction }) {
  return (
    <div className="touchStep">
      <div className="tapIcon" aria-hidden="true">{authorization ? '🔐' : '💳'}</div>
      <h3>{authorization ? 'Authorization card' : 'Customer card'}</h3>
      <p>{authorization ? 'Tap or enter an active authorization card.' : 'Tap or enter the card to complete this action.'}</p>
      <label>{authorization ? 'Authorization card ID' : 'Card ID'}<input autoFocus value={value} onChange={(event) => onChange(event.target.value)} maxLength="20" inputMode="text" /></label>
      <button type="button" className="popupClose adminPrimary touchContinue" disabled={busy || !value.trim()} onClick={onContinue}>{busy ? 'Working…' : finalAction || 'Continue'}</button>
    </div>
  );
}

function App() {
  const workflowVersion = useRef(0);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credential, setCredential] = useState('');
  const [loginMethod, setLoginMethod] = useState('pin');
  const [action, setAction] = useState(null);
  const [cardId, setCardId] = useState('');
  const [owner, setOwner] = useState('');
  const [uin, setUin] = useState('');
  const [amount, setAmount] = useState('');
  const [authorizationCardId, setAuthorizationCardId] = useState('');
  const [refundReference, setRefundReference] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [step, setStep] = useState('details');
  const [requestId, setRequestId] = useState(null);
  const [card, setCard] = useState(null);
  const [cardIdentity, setCardIdentity] = useState(null);
  const [paymentReferenceReservation, setPaymentReferenceReservation] = useState(null);
  const [paymentLocationId, setPaymentLocationId] = useState('');
  const [paymentLocations, setPaymentLocations] = useState([]);
  const [history, setHistory] = useState(null);
  const [result, setResult] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [cardPhoto, setCardPhoto] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.me()
      .then(setUser)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const login = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      setUser(await api.login(credential, loginMethod));
      setCredential('');
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    try { await api.logout(); }
    catch (requestError) { setError(requestError.message); }
    setUser(null);
    setAction(null);
  };

  const openAction = async (key) => {
    workflowVersion.current += 1;
    setAction(key);
    setError('');
    setResult(null);
    setReceipt(null);
    setCard(null);
    setCardIdentity(null);
    setPaymentReferenceReservation(null);
    setPaymentLocationId('');
    setPaymentLocations([]);
    setHistory(null);
    setCardId('');
    setOwner('');
    setUin('');
    setCardPhoto(null);
    setAmount('');
    setAuthorizationCardId('');
    setRefundReference('');
    setRefundReason('');
    setStep(key === 'check' ? 'card' : 'details');
    setRequestId(crypto.randomUUID());
    if (key === 'history') {
      try { setHistory(await api.getHistory()); }
      catch (requestError) { setError(requestError.message); }
    }
  };

  const updateOperation = (setter) => (value) => { setter(value); setRequestId(crypto.randomUUID()); setPaymentReferenceReservation(null); };

  const chooseCardPhoto = (file) => {
    setError('');
    if (!file) return setCardPhoto(null);
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setCardPhoto(null); setError('Choose a JPEG, PNG, or WebP photo no larger than 5 MB.'); return;
    }
    setCardPhoto(file);
  };

  const advanceFromDetails = () => {
    setError('');
    if ((action === 'pay' || action === 'topup') && (!amount || Number(amount) <= 0)) return setError('Enter an amount greater than zero.');
    if (action === 'create' && (!owner.trim() || !/^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/.test(uin.trim()))) return setError('Enter the card owner and a valid UIN.');
    setStep(action === 'topup' || action === 'create' ? 'authorization' : 'card');
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      if (action === 'check') setCard(await api.getBalance(cardId.trim()));
      if (action === 'create') {
        const created = await api.createCard(cardId.trim(), uin.trim(), owner.trim(), authorizationCardId.trim());
        if (cardPhoto) {
          try { await api.uploadCardPhoto(uin.trim(), cardPhoto); }
          catch (photoError) { setResult(created); throw new Error(`Card created, but the photo could not be uploaded: ${photoError.message}`); }
        }
        setResult({ ...created, photoUploaded: Boolean(cardPhoto) });
      }
      if (action === 'refund') setResult(await api.requestRefund(refundReference.trim(), refundReason.trim(), requestId));
      if (action === 'pay' || action === 'topup') {
        const response = action === 'pay'
          ? await api.recordPayment(cardId.trim(), amount, requestId, paymentReferenceReservation?.paymentReference)
          : await api.topUpBalance(cardId.trim(), amount, requestId, authorizationCardId.trim(), paymentReferenceReservation?.paymentReference);
        setResult(response);
        const version = workflowVersion.current;
        if (response.paymentReference) api.receipt(response.paymentReference)
          .then((value) => { if (workflowVersion.current === version) setReceipt(value); })
          .catch(() => { if (workflowVersion.current === version) setReceipt(null); });
      }
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  };

  const reviewCard = async () => {
    setBusy(true);
    setError('');
    try {
      if (action === 'pay') {
        const profile = await api.me();
        setUser(profile);
        if (!profile.locationId && profile.role === 'admin') {
          if (!paymentLocationId) {
            setPaymentLocations((await api.locations()).filter((location) => location.active));
            setStep('location');
            return;
          }
        } else if (!profile.locationId || !profile.locationActive) {
          throw new Error('Ask an administrator to assign you an active location before taking payment.');
        }
      }
      const identity = await api.getCardIdentity(cardId.trim());
      const nextRequestId = crypto.randomUUID();
      const reservation = await api.reservePaymentReference(
        action === 'pay' ? 'charge' : 'topup', cardId.trim(), amount, nextRequestId,
        action === 'topup' ? authorizationCardId.trim() : undefined,
        action === 'pay' && paymentLocationId ? Number(paymentLocationId) : undefined, identity.assignmentId,
      );
      setCardIdentity(identity);
      setRequestId(nextRequestId);
      setPaymentReferenceReservation(reservation);
      setStep('confirm');
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  };

  const title = actions.find((item) => item.key === action)?.label;

  return (
    <div className="App">
      <main className="App-screen">
        {loading ? <p>Checking session…</p> : !user ? (
          <section className="contentSquare loginPanel">
            <h2>{loginMethod === 'pin' ? 'Enter PIN' : loginMethod === 'admin-pin' ? 'Administrator PIN' : 'Staff login'}</h2>
            <form onSubmit={login} className="deskForm">
              <div className="loginMethod" role="group" aria-label="Sign-in method"><button type="button" aria-pressed={loginMethod === 'pin'} onClick={() => { setLoginMethod('pin'); setCredential(''); }}>PIN login</button><button type="button" aria-pressed={loginMethod === 'admin-pin'} onClick={() => { setLoginMethod('admin-pin'); setCredential(''); }}>Admin PIN</button><button type="button" aria-pressed={loginMethod === 'passphrase'} onClick={() => { setLoginMethod('passphrase'); setCredential(''); }}>Password</button></div>
              {loginMethod === 'pin' ? <PinPad value={credential} onChange={setCredential} /> : loginMethod === 'admin-pin' ? <PinPad value={credential} onChange={setCredential} length={8} label="Administrator PIN" /> : <label>Password<input type="password" inputMode="text" maxLength="200" autoComplete="current-password" value={credential} onChange={(event) => setCredential(event.target.value.toLowerCase())} required /></label>}
              {error && <p role="alert" className="adminError">{error}</p>}
              <button type="submit" className="popupClose adminPrimary touchContinue" disabled={busy || (loginMethod === 'pin' && credential.length !== 6) || (loginMethod === 'admin-pin' && credential.length !== 8)}>{busy ? 'Signing in…' : 'Sign in'}</button>
            </form>
          </section>
        ) : (
          <section className="contentSquare">
            <div className="deskHeader"><div><strong>{user.displayName || user.username}</strong><span>{user.role} role · {user.locationName || 'No location assigned'}{user.locationId && !user.locationActive ? ' (inactive)' : ''}</span></div><button type="button" className="popupClose" onClick={logout}>Sign out</button></div>
            <div className="grid">
              {actions.filter((item) => item.roles.includes(user.role)).map((item) => (
                <button key={item.key} type="button" className="gridItem" onClick={() => openAction(item.key)}>
                  <span className="gridItemIcon" aria-hidden="true">{item.icon}</span><span>{item.label}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {user && action && action !== 'admin' && (
          <div className="popupOverlay" role="dialog" aria-modal="true" aria-labelledby="action-title">
            <div className="popupModal deskModal">
              <div className="deskHeader"><h2 id="action-title">{title}</h2><button type="button" className="popupClose" onClick={() => setAction(null)}>Close</button></div>
              {action === 'refund' && !result && <div className="deskForm"><p>Enter the original charge reference. The refund is applied immediately; finance and admins are notified in the Approvals tab.</p><label>Charge reference<input autoFocus value={refundReference} onChange={(event) => updateOperation(setRefundReference)(event.target.value)} placeholder="01-26-01234-3" maxLength="24" /></label><label>Refund reason<input value={refundReason} onChange={(event) => updateOperation(setRefundReason)(event.target.value)} minLength="5" maxLength="500" placeholder="Why the refund is needed" /></label><button type="button" className="popupClose adminPrimary touchContinue" disabled={busy || !refundReference.trim() || refundReason.trim().length < 5} onClick={submit}>{busy ? 'Refunding…' : 'Issue refund now'}</button></div>}
              {action !== 'history' && action !== 'refund' && !result && !card && (
                <div className="touchFlow">
                  <div className="stepDots" aria-label="Workflow progress">{action !== 'check' && <span className={step === 'details' ? 'active' : ''}>1</span>}{(action === 'topup' || action === 'create') && <span className={step === 'authorization' ? 'active' : ''}>2</span>}<span className={step === 'card' ? 'active' : ''}>{action === 'topup' || action === 'create' ? '3' : action === 'check' ? '1' : '2'}</span></div>
                  {step === 'details' && (action === 'pay' || action === 'topup') && <><AmountPad value={amount} onChange={updateOperation(setAmount)} /><button type="button" className="popupClose adminPrimary touchContinue" onClick={advanceFromDetails}>Continue</button></>}
                  {step === 'details' && action === 'create' && <div className="touchStep"><div className="tapIcon" aria-hidden="true">👤</div><label>Owner<input autoFocus value={owner} onChange={(event) => setOwner(event.target.value)} maxLength="100" /></label><label>UIN<input value={uin} onChange={(event) => setUin(event.target.value)} maxLength="64" placeholder="Cross-platform person ID" /></label><CardPhotoChooser photo={cardPhoto} onChoose={chooseCardPhoto} onError={setError} /><button type="button" className="popupClose adminPrimary touchContinue" onClick={advanceFromDetails}>Continue</button></div>}
                  {step === 'authorization' && <CardTouchStep authorization value={authorizationCardId} onChange={updateOperation(setAuthorizationCardId)} onContinue={() => setStep('card')} busy={busy} />}
                  {step === 'card' && <CardTouchStep value={cardId} onChange={(value) => { updateOperation(setCardId)(value); setCardIdentity(null); }} onContinue={action === 'pay' || action === 'topup' ? reviewCard : submit} busy={busy} finalAction={action === 'pay' || action === 'topup' ? 'Review cardholder' : action === 'check' ? 'Check card' : 'Create card'} />}
                  {step === 'location' && <div className="touchStep"><p>Choose an active location for this payment before reserving its reference.</p><label>Payment location<select value={paymentLocationId} onChange={(event) => setPaymentLocationId(event.target.value)}><option value="">Choose location</option>{paymentLocations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label><button type="button" className="popupClose adminPrimary touchContinue" disabled={busy || !paymentLocationId} onClick={reviewCard}>Review payment</button></div>}
                  {step === 'confirm' && cardIdentity && <div className="identityReview"><p className="identityEyebrow">Confirm cardholder</p>{action === 'pay' && <p>Payment location: {paymentReferenceReservation?.locationName || user.locationName || paymentLocations.find((location) => String(location.id) === paymentLocationId)?.name}</p>}<div className="identityReviewHeader">{cardIdentity.photoPath ? <img className="identityReviewPortrait" src={cardIdentity.photoPath} alt={`${cardIdentity.owner} ID portrait`} /> : <div className="identityReviewPortrait identityPlaceholder" aria-label="No portrait on file">👤</div>}<div className="identityReviewInfo"><h3>{cardIdentity.owner}</h3><p>UIN {cardIdentity.uin}</p><p>Card {cardIdentity.cardId}</p></div></div><strong className="identityAmount" aria-label={action === 'topup' ? 'Top-up amount' : 'Charge amount'}>{Number(amount).toFixed(2)} THB</strong>{paymentReferenceReservation && <div className="reservedPaymentReference"><span>Cross-reference before confirming</span><code>{paymentReferenceReservation.paymentReference}</code><small>Reserved for this transaction · expires {new Date(paymentReferenceReservation.expiresAt).toLocaleString('en-GB', { timeZone: 'Asia/Bangkok' })}</small></div>}<button type="button" className="popupClose adminPrimary touchContinue" disabled={busy || !paymentReferenceReservation} onClick={submit}>{busy ? 'Working…' : action === 'topup' ? 'Confirm top-up' : 'Confirm charge'}</button></div>}
                  {step !== 'details' && action !== 'check' && <button type="button" className="backButton" onClick={() => setStep(step === 'confirm' || step === 'location' ? 'card' : step === 'card' && (action === 'topup' || action === 'create') ? 'authorization' : 'details')}>← Back</button>}
                </div>
              )}
              {result && <div role="status" className="adminSuccess"><strong>{result.type === 'refund' ? 'Refund completed' : 'Completed'}</strong>{result.type === 'refund' && <><p>{Number(result.amount).toFixed(2)} THB returned to {result.owner} · card {result.cardId}</p><p>Original charge: <code className="eventRef">{result.originalPaymentReference}</code></p></>}<p>{result.paymentReference ? 'Payment reference' : 'Event reference'}: <code className="eventRef">{result.paymentReference || result.eventId}</code></p>{result.balance != null && <p>Balance: {Number(result.balance).toFixed(2)} THB</p>}{result.photoUploaded && <p>Cardholder photo uploaded.</p>}{result.replayed && <p>Previously completed request returned safely.</p>}</div>}
              {result && action === 'pay' && <button type="button" className="popupClose" onClick={() => openAction('pay')}>Next payment</button>}
              {receipt && <Receipt receipt={receipt} />}
              {card && <div className="cardDetails">{card.photoPath && <img className="cardPortrait" src={card.photoPath} alt={`${card.owner} ID portrait`} />}<h3>{card.owner} · {card.cardId}</h3><p>UIN: <code>{card.uin}</code></p><strong>{Number(card.balance).toFixed(2)} THB</strong><p>Status: {card.status}</p><h4>Recent card events</h4>{card.recentActivity.length ? card.recentActivity.map((entry) => <p key={entry.event_id}><code className="eventRef">{entry.payment_reference || entry.event_id}</code> · {entry.type} · {Number(entry.amount).toFixed(2)} THB</p>) : <p>No events yet.</p>}</div>}
              {history && <div className="adminTableWrap"><table className="historyTable"><thead><tr><th>Time</th><th>Type</th><th>Amount</th><th>Card / Owner</th></tr></thead><tbody>{history.map((entry) => <tr key={entry.event_id} className={historyRowClass(entry.type)}><td>{formatHistoryTime(entry.created_at)}</td><td>{entry.type.replaceAll('_', ' ')}</td><td>{Number(entry.amount).toFixed(2)} THB</td><td>{entry.card_id && <><strong>{entry.card_id}</strong> · </>}{entry.owner || 'Unknown owner'}</td></tr>)}</tbody></table>{history.length === 0 && <p>No events yet.</p>}</div>}
            </div>
          {error && <div className="actionErrorLayer"><div className="actionErrorCard"><p role="alert">{error}</p><button type="button" className="popupClose" onClick={() => setError('')}>Dismiss</button></div></div>}
          </div>
        )}
        {user && action === 'admin' && <AdminPanel role={user.role} onClose={() => setAction(null)} />}
      </main>
    </div>
  );
}

export default App;
